<template>
  <nav
    v-if="fields"
    class="c-article-pagination"
    :style="rootStyle"
    aria-label="Article pagination"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-article-pagination__inner">
        <a
          v-if="previousTitle"
          class="c-article-pagination__link c-article-pagination__link--previous"
          :href="previousHref"
          :target="previousTarget"
          :aria-label="`Previous article: ${previousTitle}`"
        >
          <span class="c-article-pagination__arrow c-article-pagination__arrow--previous" />
          <span class="c-article-pagination__title">{{ previousTitle }}</span>
        </a>

        <a
          v-if="nextTitle"
          class="c-article-pagination__link c-article-pagination__link--next"
          :href="nextHref"
          :target="nextTarget"
          :aria-label="`Next article: ${nextTitle}`"
        >
          <span class="c-article-pagination__title">{{ nextTitle }}</span>
          <span class="c-article-pagination__arrow c-article-pagination__arrow--next" />
        </a>
      </div>
    </ContentGrid>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ArticlePaginationProps } from '@/types/components/ArticlePagination';
import utils from '@/utils';

const props = defineProps<ArticlePaginationProps>();
const fields = computed(() => props.fields);

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const previousLink = computed(() => fields.value?.previousLink?.value || {});
const nextLink = computed(() => fields.value?.nextLink?.value || {});

const previousTitle = computed(
  () =>
    fields.value?.previousTitle?.value || previousLink.value.text || previousLink.value.title || ''
);
const nextTitle = computed(
  () => fields.value?.nextTitle?.value || nextLink.value.text || nextLink.value.title || ''
);

const previousHref = computed(() => previousLink.value.href || '#');
const nextHref = computed(() => nextLink.value.href || '#');
const previousTarget = computed(() => previousLink.value.target || undefined);
const nextTarget = computed(() => nextLink.value.target || undefined);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-article-pagination {
  position: relative;
  width: 100%;
  background: $white;

  &__inner {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  &__link {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    min-height: 42px;
    color: #404040;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      color: #739b78;
      outline: none;
    }

    &--next {
      grid-template-columns: minmax(0, 1fr) 20px;
      color: #739b78;
      text-align: left;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 3px;
    }
  }

  &__title {
    display: block;
    overflow: hidden;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
    text-overflow: ellipsis;
  }

  &__arrow {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    color: currentColor;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 10px;
      height: 10px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
    }

    &--previous::before {
      transform: translate(-35%, -50%) rotate(-135deg);
    }

    &--next::before {
      transform: translate(-65%, -50%) rotate(45deg);
    }
  }

  @include tablet {
    &__inner {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      align-items: center;
      gap: 24px;
      min-height: 66px;
    }

    &__link {
      min-height: 66px;
    }

    &__link--next {
      justify-self: end;
      text-align: right;
    }

    &__title {
      font-size: 18px;
    }
  }

  @include tablet-landscape {
    &__title {
      font-size: 18px;
    }
  }
}
</style>
